import { useEffect } from "react";
import { RootState } from "@/redux/store";
import { useSelector } from "react-redux";
import {
  ContentType,
  DimensionOutroSummary,
} from "@dimensional-engineering/dimensional-models";

import styles from "./ConnectedDimensionSummaryModal/ConnectedDimensionSummaryModal.module.css";

import {
  dimensionData,
  dimensionIcons,
} from "@/assets/traitInfo/dimensionData";
import { ScoreAndDescriptorFullWidth } from "./ScoreAndDescriptor/ScoreAndDescriptor";
import TwoByTwoSummary, {
  TwoByTwoSummaryNormalized,
} from "./TwoByTwo/TwoByTwoSummary";
import { Mixpanel } from "@/helpers/mixpanel";
import LoadingScreen from "@/components/assessments-new/views/AsessmentOutroScreen/loadingScreen/LoadingScreen";
import ModalWrapper from "@/components/shared/ModalWrapper/ModalWrapper";
import LargeProfileImage from "@/components/shared/SmallProfileImage/LargeProfileImage";
import Spectrums from "@/components/shared/Spectrum/Spectrums";
import SignatureNonSignatureNew from "./SignatureNonSignatureNew/SignatureNonSignatureNew";
import {
  PeriodicQuizInstagramIcon,
  PeriodicQuizSnapchatIcon,
} from "@/components/appHome/PeriodicQuizzes/PeriodicQuizShareIcons";
import { DimensionResultOutroInterractionBar } from "../Snippets/SnippetInterractionBar/SnippetInterractionBar";
import { useModalContext } from "@/context/ModalContext";
import DimensionSummariesShareView from "./DimensionSummariesShareView/DimensionSummariesShareView";
import GoDeeperModal from "@/components/GoDeeper/GoDeeperModal/GoDeeperModal";
import GoDeeperIcon from "@/components/GoDeeper/GoDeeperIcon";
import { useAuth } from "@/context/AuthContext";

type Props = {
  slug: string;
  outro: DimensionOutroSummary | null;
  onContinue: () => void;
};

export default function DimensionSummariesOutro(props: Props) {
  const { setModalComponent } = useModalContext();
  const { user } = useAuth();

  const userState = useSelector((state: RootState) => state.user);

  const dimension = dimensionData?.find(
    (d) => d.slug === props.slug || d.longFormSlug === props.slug
  );
  const summary = userState.dimensionSummaries?.find(
    (s) => s.dimensionSlug === props.slug
  );

  useEffect(() => {
    Mixpanel?.track(
      "Dimension Summary Viewed",
      {
        dimension_slug: props.slug,
      },
      user?.uid
    );
  }, []);

  const summaryMapper = props.outro?.dimensionSummary?.summaryViews.map(
    (summ, index) => {
      if (summ.summaryType === "dichotomyArchetype") {
        return (
          <div className={styles.spectrumsDiv} key={index}>
            <Spectrums
              contentIdentifier={{
                contentType: ContentType.dimensionSummary,
                contentSlug: props.slug,
              }}
              otherPublicProfile={null}
              noMargin
            />
          </div>
        );
      }
      if (summ.summaryType === "scoreAndDescriptor") {
        return (
          <div className={styles.scoreAndDescDiv} key={index}>
            <ScoreAndDescriptorFullWidth {...summ.summaryData} />
          </div>
        );
      }
      if (summ.summaryType === "signatureNonSignature") {
        return (
          <SignatureNonSignatureNew
            key={index}
            slug={props.slug}
            data={summ.summaryData}
          />
        );
      }
      if (summ.summaryType === "twoByTwoGrid") {
        if (props.slug === "communication-style") {
          return (
            <div className={styles.twoByTwoDiv} key={index}>
              <TwoByTwoSummaryNormalized {...summ.summaryData} />
            </div>
          );
        }
        return (
          <div className={styles.twoByTwoDiv} key={index}>
            <TwoByTwoSummary {...summ.summaryData} />
          </div>
        );
      }
    }
  );

  const mappedDescription = dimension?.summaryDescription.map((s, i) => {
    return (
      <p className={styles.dimensionDetailsDesc} key={i}>
        {s}
      </p>
    );
  });

  const mappedSuggestedMessages = dimension?.suggestedMessages.map(
    (message, i) => {
      return (
        <div
          onClick={() => {
            Mixpanel?.track(
              "Go deeper initial message selected",
              {
                body: message,
                content_type: "dimension summary",
                slug: props.slug,
              },
              user?.uid
            );
            setModalComponent(
              <GoDeeperModal
                dimensionSummaryData={{
                  dimensionSlug: props.slug,
                  title: dimension?.title ?? "",
                  suggestedMessage: message,
                }}
              />
            );
          }}
          className={styles.suggestedMessageDiv}
          key={i}
        >
          <p className={styles.suggestedMessage}>{message}</p>
        </div>
      );
    }
  );

  if (!props.outro) {
    return (
      <ModalWrapper noHeader noLine title="">
        <LoadingScreen message="" />
      </ModalWrapper>
    );
  }

  return (
    <main className={styles.main}>
      <section className={styles.top}>
        <LargeProfileImage
          publicProfile={userState.publicProfileData}
          imageUrl={userState.imageURL as string}
          noLink
        />
        <div className={styles.iconDiv}>{dimensionIcons[props.slug].icon}</div>
      </section>

      <section className={styles.titleAndDescSection}>
        <h1 className={styles.title}>
          Dimension Results: <br /> {dimension?.title}
        </h1>
        <p className={styles.desc}>{dimension?.description}</p>
      </section>

      <section className={styles.summarySection}>{summaryMapper}</section>

      <section className={styles.socialSection}>
        <div className={styles.socialButtonsDiv}>
          <PeriodicQuizSnapchatIcon
            onClick={() => {
              setModalComponent(
                <DimensionSummariesShareView
                  slug={props.slug}
                  data={summary}
                  type="snapchat"
                />
              );
              Mixpanel?.track(
                "Shared to Social",
                {
                  type: "dimensionSummary",
                  slug: props.slug,
                  platform: "Snapchat",
                },
                user?.uid
              );
            }}
          />
          <PeriodicQuizInstagramIcon
            onClick={() => {
              setModalComponent(
                <DimensionSummariesShareView
                  slug={props.slug}
                  data={summary}
                  type="instagram"
                />
              );
              Mixpanel?.track(
                "Shared to Social",
                {
                  type: "dimensionSummary",
                  slug: props.slug,
                  platform: "Instagram",
                },
                user?.uid
              );
            }}
          />
        </div>
        <p className={styles.shareToText}>Share to</p>
      </section>

      <section className={styles.descriptionSection}>
        <h2 className={styles.dimensionDetailsTitle}>Dimension details</h2>
        {mappedDescription}
      </section>
      {mappedSuggestedMessages?.length ? (
        <section className={styles.suggestedMessagesSection}>
          <div className={styles.suggestedMessagesTitleDiv}>
            <h1 className={styles.suggestedMessagesTitle}>Go deeper</h1>
            <GoDeeperIcon />
          </div>
          <div className={styles.suggestedMessagesWrapper}>
            {mappedSuggestedMessages}
          </div>
        </section>
      ) : null}
      <div style={{ position: "fixed" }} className={styles.interractionBarDiv}>
        <DimensionResultOutroInterractionBar
          onContinue={() => props.onContinue()}
        />
      </div>
    </main>
  );
}
